import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlarmClock, X, Sun } from "lucide-react";
import { playAmbient, stopCurrent, AMBIENT_SOUNDS } from "../services/ambientAudio";

/**
 * AlarmRinging — pagina mostrata quando l'utente tocca la notifica di sveglia.
 *
 * Comportamento:
 *  - All'apertura, avvia in loop il suono ambient passato come query param
 *    `?sound=<id>` (es. ?sound=rain). Se non fornito → default "rain".
 *  - Bottone "Ferma sveglia" → stop loop + naviga al riepilogo.
 */
export default function AlarmRinging() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [now, setNow] = useState(() => new Date());

  const soundId = params.get("sound") || "rain";
  const soundDef = useMemo(
    () => AMBIENT_SOUNDS.find((s) => s.id === soundId) || AMBIENT_SOUNDS[0],
    [soundId]
  );

  useEffect(() => {
    (async () => {
      await playAmbient(soundDef.id);
    })();
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(t);
      stopCurrent();
    };
  }, [soundDef.id]);

  const timeStr = now.toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-between text-white relative overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, #0a3b3a 0%, #0f2b3a 45%, #152439 80%, #1a0a1f 100%)",
      }}
      data-testid="alarm-ringing-screen"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 28%, rgba(20,194,161,0.35), rgba(20,194,161,0) 60%)",
          animation: "pulseRing 2.4s ease-in-out infinite",
        }}
      />

      <div className="pt-16 flex flex-col items-center relative z-10">
        <div className="flex items-center gap-2 text-[#67e8d1] text-[12px] uppercase tracking-[0.35em]">
          <AlarmClock size={14} />
          Sveglia
        </div>
        <div className="mt-4 text-white text-[78px] font-light leading-none tracking-tight font-mono">
          {timeStr}
        </div>
        <div className="mt-3 text-white/70 text-[14px]">Buongiorno!</div>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-1">
        <div
          className="w-20 h-20 rounded-full bg-[#14c2a1]/15 border border-[#14c2a1]/40 flex items-center justify-center"
          style={{ animation: "pulseRing 1.8s ease-in-out infinite" }}
        >
          <Sun size={36} className="text-[#67e8d1]" />
        </div>
        <div className="text-white/70 text-[13px] mt-3">
          In riproduzione: <span className="text-white">{soundDef.name}</span>
        </div>
      </div>

      <div className="w-full max-w-[400px] px-6 pb-12 relative z-10">
        <button
          onClick={async () => {
            await stopCurrent();
            navigate("/riepilogo", { replace: true });
          }}
          data-testid="alarm-stop-btn"
          className="w-full h-14 rounded-full bg-white text-[#0a2030] text-[17px] font-semibold tracking-wide hover:bg-white/90 active:scale-[0.98] transition-all flex items-center justify-center gap-3 shadow-[0_10px_30px_-12px_rgba(255,255,255,0.4)]"
        >
          <X size={20} /> Ferma sveglia
        </button>
      </div>
    </div>
  );
}
